/* CHARGEBEE API */
import { captureSentryGenericError } from '../../error-handling/sentry';

let didInitChargebee = false;

const ensureChargebeeInit = () => {
  const Chargebee = window?.Chargebee;
  if (!Chargebee) return null;

  if (!didInitChargebee && !window.Cypress) {
    try {
      Chargebee.init({
        site: import.meta.env.REACT_APP_CB_SITE,
        enableGATracking: true,
      });
    } catch (e) {
      // may still throw if called on top of initialized instance
    }
    didInitChargebee = true;
  }

  return Chargebee;
};

const getChargebeeInstance = () => {
  const Chargebee = ensureChargebeeInit();
  return Chargebee?.getInstance?.();
};

/** open portal session for customer to manage billing info through ChargeBee with optional config
 * SEE HERE FOR DETAILS:
 * https://www.chargebee.com/checkout-portal-docs/api.html#chargebee-portal-instance-object
 * @param {Function} makeCustomerPortal -> mutation function to return portal promise
 * @param {String} accountId -> id of subscription/account to open portal for
 * @param {Object} Chargebee -> chargebee instance to use cb methods
 * @param {Object} section -> string of section to open
 * @param {Object} onClose -> callback to run on close of portal
 * @param {Object} onComplete -> callback for successful action in portal (card add/edit)
 */
export const handlePortalOpenSection = (
  makeCustomerPortal, accountId, section,
  onClose, onComplete,
) => {
  const Chargebee = ensureChargebeeInit();
  const chargebeeInstance = getChargebeeInstance();
  if (!chargebeeInstance || !Chargebee) return;

  chargebeeInstance.setPortalSession(async () => {
    try {
      const mutationResult = await makeCustomerPortal();
      const session = mutationResult?.data?.generateCustomerPortal;
      if (!session) throw new Error('No portal session returned');
      return session;
    } catch (e) {
      captureSentryGenericError(e, { context: 'chargebee-portal-session', section });
      chargebeeInstance.logout();
      throw e;
    }
  });
  chargebeeInstance.createChargebeePortal().openSection(
    {
      sectionType: Chargebee.getPortalSections()[section],
      params: {
        subscriptionId: accountId,
      },
    },
    {
      close() {
        chargebeeInstance.logout();
        if (onClose) {
          onClose();
        }
      },
      paymentSourceAdd(status) {
        if (status === 'success') {
          onComplete?.();
          chargebeeInstance.closeAll();
        }
      },
      paymentSourceUpdate(status) {
        if (status === 'success') {
          onComplete?.();
          chargebeeInstance.closeAll();
        }
      },
    },
  );
};

/** Prompt user with portal to checkout the plan they've selected, optionally
 * can disable trial for the plan
 * @param {Function} makeCheckoutPortal -> mutation function to return portal promise
 * @param {String} planIdToPurchase -> Id of CB add-on for one-time checkout
 * (must be associated with user's plan)
 * @param {Boolean} disableTrial -> boolean indicating if trial should not be applied to plan
 * (only works if it's a trial-enabled plan)
 * @param {Function} successCallback -> callback function to call on successful checkout
 */
export const handlePlanCheckoutSession = async (
  makeCheckoutPortal, planIdToPurchase, disableTrial, successCallback,
) => {
  const chargebeeInstance = getChargebeeInstance();
  if (!chargebeeInstance) return;
  // generate portal
  chargebeeInstance.openCheckout({
    hostedPage: async () => {
      try {
        const mutationResult = await makeCheckoutPortal({
          variables: {
            planId: planIdToPurchase,
            disableTrial,
          },
        });
        const hostedPage = mutationResult?.data?.generateCheckoutPortal;
        if (!hostedPage) throw new Error('No checkout page returned');
        return hostedPage;
      } catch (e) {
        captureSentryGenericError(e, { context: 'chargebee-plan-checkout', planId: planIdToPurchase });
        chargebeeInstance.logout();
        throw e;
      }
    },
    success: async (hostedPageId) => {
      if (successCallback) {
        await successCallback(hostedPageId); // supply hosted page id to callback
      }
      chargebeeInstance.closeAll(); // close all cb instance windows
    },
    close: () => {
      chargebeeInstance.logout(); // end cb session on close
    },
  });
};

/** Prompt user with portal to upgrade their existing plan to a new one
 * @param {Function} makeUpgradePortal -> mutation function to return portal promise
 * @param {String} accountId -> id of subscription/account to open portal for
 * @param {String} newPlanId -> Id of CB add-on for one-time checkout
 * (must be associated with user's plan)
 * @param {Function} successCallback -> callback function to call on successful checkout
 */
export const handlePlanUpgradeSession = async (
  makeUpgradePortal, accountId, newPlanId, successCallback,
) => {
  const chargebeeInstance = getChargebeeInstance();
  if (!chargebeeInstance) return;
  // generate portal
  chargebeeInstance.openCheckout({
    hostedPage: async () => {
      try {
        const mutationResult = await makeUpgradePortal({
          variables: {
            planId: newPlanId,
            accountId,
          },
        });
        const hostedPage = mutationResult?.data?.generateUpgradePortal;
        if (!hostedPage) throw new Error('No upgrade page returned');
        return hostedPage;
      } catch (e) {
        captureSentryGenericError(e, { context: 'chargebee-plan-upgrade', planId: newPlanId, accountId });
        chargebeeInstance.logout();
        throw e;
      }
    },
    success: async (hostedPageId) => {
      if (successCallback) {
        successCallback(hostedPageId); // supply hosted page id to callback
      }
      chargebeeInstance.closeAll(); // close all cb instance windows
    },
    close: () => {
      chargebeeInstance.logout(); // end cb session on close
    },
  });
};

/** Prompt user with portal to upgrade their existing plan to a new one
 * @param {Function} makeOneTimeCheckout -> mutation function to return portal promise
 * @param {String} accountId -> id of subscription/account to open portal for
 * @param {String} addOnId -> Id of CB add-on for one-time checkout
 * (must be associated with user's plan)
 * @param {Function} successCallback -> callback function to call on successful checkout
 */
export const handleOneTimeCheckout = async (
  makeOneTimeCheckout, accountId, addOnId, successCallback,
) => {
  const chargebeeInstance = getChargebeeInstance();
  if (!chargebeeInstance) return;

  chargebeeInstance.openCheckout({
    hostedPage: async () => {
      try {
        const mutationResult = await makeOneTimeCheckout({
          variables: {
            addOnId,
            accountId,
          },
        });
        const hostedPage = mutationResult?.data?.generateCheckoutAddonPortal;
        if (!hostedPage) throw new Error('No addon checkout page returned');
        return hostedPage;
      } catch (e) {
        captureSentryGenericError(e, { context: 'chargebee-one-time-checkout', addOnId, accountId });
        chargebeeInstance.logout();
        throw e;
      }
    },
    success: async (hostedPageId) => {
      if (successCallback) {
        successCallback(hostedPageId); // supply hosted page id to callback
      }
      chargebeeInstance.closeAll(); // close all cb instance windows
    },
    close: () => {
      chargebeeInstance.logout(); // end cb session on close
    },
  });
};
